import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  adminListUsers,
  adminGetStats,
  adminToggleAdmin,
  adminDeleteUser,
  adminUpdateUserType,
  adminUpdateUserStatus,
  adminUpdateVineyardStatus,
  adminUpdateWineStatus,
  adminGetPendingUsers,
  adminGetPendingVineyards,
  adminGetPendingWines,
  adminGetSeedStatus,
  adminForceSeed,
  adminRunDrinkSoonDigest,
  adminTestEmail,
} from '../lib/api';
import { USER_TYPES, USER_TYPE_BY_VALUE } from '../lib/userTypes';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Users, MapPin, Wine, Star, Heart, TrendingUp, Trash2, Crown,
  CheckCircle2, XCircle, Clock, Grape, DatabaseBackup, Mail, Send,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, accent = '#d4af37' }) => (
  <div className="p-6 bg-stone-900/30 border border-white/5" data-testid={`stat-${label.toLowerCase().replace(/\W+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-10 h-10 flex items-center justify-center border"
        style={{ borderColor: `${accent}33`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
    </div>
    <p className="text-3xl font-serif text-stone-50">{value}</p>
  </div>
);

const PendingSection = ({ title, icon: Icon, items, emptyText, renderItem, onApprove, onReject, onView, testIdPrefix }) => (
  <div data-testid={`${testIdPrefix}-section`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#d4af37]" />
      <h3 className="text-xs uppercase tracking-widest text-[#d4af37]">{title}</h3>
      <span className="text-xs text-stone-600">({items.length})</span>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-stone-600 italic p-6 bg-stone-900/20 border border-white/5">{emptyText}</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div
            key={it.id}
            className="p-4 bg-stone-900/30 border border-[#d4af37]/20 flex flex-col gap-3"
            data-testid={`${testIdPrefix}-${it.id}`}
          >
            <div className="flex-1">{renderItem(it)}</div>
            <div className="flex gap-2 pt-2 border-t border-white/5">
              <Button
                size="sm"
                onClick={() => onApprove(it)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none uppercase tracking-widest text-xs h-8"
                data-testid={`${testIdPrefix}-approve-${it.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                onClick={() => onReject(it)}
                variant="outline"
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-none uppercase tracking-widest text-xs h-8"
                data-testid={`${testIdPrefix}-reject-${it.id}`}
              >
                <XCircle className="w-3 h-3 mr-1" /> Reject
              </Button>
              {onView && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onView(it)}
                  className="text-stone-400 hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs h-8"
                  data-testid={`${testIdPrefix}-view-${it.id}`}
                >
                  View
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingVineyards, setPendingVineyards] = useState([]);
  const [pendingWines, setPendingWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [seedStatus, setSeedStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [digesting, setDigesting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const load = async () => {
    try {
      const [s, u, pu, pv, pw, ss] = await Promise.all([
        adminGetStats(),
        adminListUsers(),
        adminGetPendingUsers(),
        adminGetPendingVineyards(),
        adminGetPendingWines(),
        adminGetSeedStatus().catch(() => null),
      ]);
      setStats(s);
      setUsers(u);
      setPendingUsers(pu);
      setPendingVineyards(pv);
      setPendingWines(pw);
      setSeedStatus(ss);
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreCatalog = async (force = false) => {
    if (!seedStatus?.seed_file_available) {
      toast.error('No seed file available in this deployment');
      return;
    }
    const currentTotal = (seedStatus.current_vineyards || 0) + (seedStatus.current_wines || 0);
    if (currentTotal > 0 && !force) {
      const ok = window.confirm(
        `Catalog already has ${seedStatus.current_vineyards} vineyards and ${seedStatus.current_wines} wines. ` +
        `Restore will add any missing items from the bundled seed (existing items are kept). Continue?`
      );
      if (!ok) return;
      force = true;
    }
    setSeeding(true);
    try {
      const result = await adminForceSeed(force);
      if (result.ok) {
        toast.success(
          `Restored: +${result.inserted_vineyards} vineyards, +${result.inserted_wines} wines. ` +
          `Totals: ${result.total_vineyards} / ${result.total_wines}.`
        );
        const ss = await adminGetSeedStatus().catch(() => null);
        setSeedStatus(ss);
        const s = await adminGetStats().catch(() => null);
        if (s) setStats(s);
      } else {
        toast.message(result.message || 'Nothing seeded');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setSeeding(false);
    }
  };

  const toggleAdmin = async (u, next) => {
    setPendingId(u.id);
    try {
      const updated = await adminToggleAdmin(u.id, next);
      setUsers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toast.success(`${updated.email} ${next ? 'promoted to admin' : 'demoted to user'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update flag');
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminDeleteUser(confirmDelete.id);
      setUsers((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      toast.success('User deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete user');
    } finally {
      setConfirmDelete(null);
    }
  };

  const approveUser = async (u, status) => {
    try {
      await adminUpdateUserStatus(u.id, status);
      toast.success(`User ${status}`);
      setPendingUsers((prev) => prev.filter((x) => x.id !== u.id));
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status } : x)));
      if (stats) setStats({ ...stats, pending_users: Math.max(0, stats.pending_users - 1), pending_total: Math.max(0, stats.pending_total - 1) });
    } catch (e) {
      toast.error('Could not update user status');
    }
  };

  const approveVineyard = async (v, status) => {
    try {
      await adminUpdateVineyardStatus(v.id, status);
      toast.success(`Vineyard ${status}`);
      setPendingVineyards((prev) => prev.filter((x) => x.id !== v.id));
      if (stats) setStats({ ...stats, pending_vineyards: Math.max(0, stats.pending_vineyards - 1), pending_total: Math.max(0, stats.pending_total - 1) });
    } catch (e) {
      toast.error('Could not update vineyard status');
    }
  };

  const approveWine = async (w, status) => {
    try {
      await adminUpdateWineStatus(w.id, status);
      toast.success(`Wine ${status}`);
      setPendingWines((prev) => prev.filter((x) => x.id !== w.id));
      if (stats) setStats({ ...stats, pending_wines: Math.max(0, stats.pending_wines - 1), pending_total: Math.max(0, stats.pending_total - 1) });
    } catch (e) {
      toast.error('Could not update wine status');
    }
  };

  const changeUserType = async (u, newType) => {
    if (newType === u.user_type) return;
    try {
      const updated = await adminUpdateUserType(u.id, newType);
      setUsers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toast.success(`${updated.email} → ${USER_TYPE_BY_VALUE[newType]?.label || newType}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change user type');
    }
  };

  const handleSendTestEmail = async () => {
    setEmailing(true);
    try {
      const result = await adminTestEmail();
      if (result.ok) {
        toast.success(`Test email sent to ${result.to}`);
      } else {
        toast.error('Test email failed — check backend logs and RESEND_API_KEY');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setEmailing(false);
    }
  };

  const handleRunDigest = async () => {
    const ok = window.confirm('Send the "Drink Soon" digest to all eligible users now?');
    if (!ok) return;
    setDigesting(true);
    try {
      const result = await adminRunDrinkSoonDigest();
      toast.success(
        `Digest complete: ${result.users_emailed} emailed, ${result.users_skipped} skipped (threshold ${result.threshold_year}).`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest failed');
    } finally {
      setDigesting(false);
    }
  };

  const filteredUsers = typeFilter === 'all'
    ? users
    : users.filter((u) => (u.user_type || 'public') === typeFilter);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="admin-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2 flex items-center gap-2">
            <Shield className="w-3 h-3" /> Administration
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-50 flex items-center gap-3">
            Admin Dashboard
          </h1>
          <p className="text-stone-500 text-sm mt-2">
            Signed in as <span className="text-[#d4af37]">{user?.email}</span>
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="admin-stats-grid">
            <StatCard icon={Users} label="Users" value={stats.users} />
            <StatCard icon={Crown} label="Admins" value={stats.admins} />
            <StatCard icon={MapPin} label="Vineyards" value={stats.vineyards} />
            <StatCard icon={Wine} label="Wines" value={stats.wines} />
            <StatCard icon={Star} label="Total Ratings" value={stats.vineyard_ratings + stats.wine_ratings} />
            <StatCard icon={Heart} label="Favorites" value={stats.favorites} />
            <StatCard icon={TrendingUp} label="Ratings (7d)" value={stats.recent_ratings_7d} accent="#9f1239" />
            <StatCard icon={Clock} label="Pending Approvals" value={stats.pending_total || 0} accent={stats.pending_total > 0 ? '#f59e0b' : '#d4af37'} />
          </div>
        )}

        {/* Catalog Restore (Admin) */}
        {seedStatus && (
          <div
            className="mb-12 p-6 bg-stone-900/30 border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4"
            data-testid="catalog-restore-panel"
          >
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 flex items-center justify-center border shrink-0"
                style={{ borderColor: '#d4af3733', color: '#d4af37' }}
              >
                <DatabaseBackup className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Catalog Restore</p>
                <p className="text-sm text-stone-300">
                  Current: <span className="text-[#d4af37]">{seedStatus.current_vineyards}</span> vineyards,{' '}
                  <span className="text-[#d4af37]">{seedStatus.current_wines}</span> wines.{' '}
                  {seedStatus.seed_file_available ? (
                    <>
                      Bundled seed available (
                      <span className="text-stone-400">{seedStatus.seed_vineyards}</span> vineyards,{' '}
                      <span className="text-stone-400">{seedStatus.seed_wines}</span> wines).
                    </>
                  ) : (
                    <span className="text-amber-400">No seed file in this deployment.</span>
                  )}
                </p>
              </div>
            </div>
            <Button
              onClick={() => handleRestoreCatalog(false)}
              disabled={!seedStatus.seed_file_available || seeding}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-6 py-5 disabled:opacity-50"
              data-testid="restore-catalog-btn"
            >
              {seeding ? 'Restoring…' : 'Restore Catalog'}
            </Button>
          </div>
        )}

        {/* Email Notifications (Admin) */}
        <div
          className="mb-12 p-6 bg-stone-900/30 border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4"
          data-testid="email-panel"
        >
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 flex items-center justify-center border shrink-0"
              style={{ borderColor: '#d4af3733', color: '#d4af37' }}
            >
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Email Notifications</p>
              <p className="text-sm text-stone-300">
                Sender: <span className="text-[#d4af37]">noreply@vineyardswine.com</span>. Verify the integration,
                then trigger the &ldquo;Drink Soon&rdquo; digest for all eligible users.
              </p>
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button
              onClick={handleSendTestEmail}
              disabled={emailing}
              variant="outline"
              className="bg-transparent border border-stone-700 text-stone-300 hover:border-[#d4af37] hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs px-5 py-5 disabled:opacity-50"
              data-testid="send-test-email-btn"
            >
              <Send className="w-3 h-3 mr-2" /> {emailing ? 'Sending…' : 'Test Email'}
            </Button>
            <Button
              onClick={handleRunDigest}
              disabled={digesting}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-5 py-5 disabled:opacity-50"
              data-testid="run-digest-btn"
            >
              {digesting ? 'Running…' : 'Run Drink Soon Digest'}
            </Button>
          </div>
        </div>

        {/* Tabbed Management: Users / Pending Queues */}
        <Tabs defaultValue={stats?.pending_total > 0 ? 'pending' : 'users'} className="space-y-6">
          <TabsList className="bg-stone-900/50 border border-white/5 rounded-none h-auto p-1">
            <TabsTrigger
              value="users"
              className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950 uppercase tracking-widest text-xs"
              data-testid="tab-users"
            >
              <Users className="w-3 h-3 mr-2" /> All Users
              <span className="ml-2 text-[10px] opacity-70">{users.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="pending"
              className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950 uppercase tracking-widest text-xs"
              data-testid="tab-pending"
            >
              <Clock className="w-3 h-3 mr-2" /> Pending Approvals
              {stats?.pending_total > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-red-500/30 text-red-300 text-[10px] rounded-sm">
                  {stats.pending_total}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xs uppercase tracking-widest text-[#d4af37] mb-1">Users</h2>
                <p className="text-2xl font-serif text-stone-50">All Registered Accounts</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-widest text-stone-500">Filter Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger
                    className="bg-stone-900 border-stone-700 rounded-none h-9 w-44"
                    data-testid="user-type-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    <SelectItem value="all">All types</SelectItem>
                    {USER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-stone-500">{filteredUsers.length} shown</span>
              </div>
            </div>
            <div className="overflow-x-auto border border-white/5" data-testid="users-table">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-stone-900/60 border-b border-white/10">
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">User</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Type</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Joined</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Content</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Admin</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const isSelf = u.id === user?.id;
                    const status = u.status || 'approved';
                    const userType = u.user_type || 'public';
                    return (
                      <tr
                        key={u.id}
                        className="border-b border-white/5 hover:bg-stone-900/30 transition-colors"
                        data-testid={`user-row-${u.id}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-stone-800 flex items-center justify-center text-[#d4af37] font-serif">
                              {u.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="text-stone-50 flex items-center gap-2">
                                {u.name}
                                {u.is_admin && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-[10px] uppercase tracking-widest">
                                    <Crown className="w-2.5 h-2.5" /> Admin
                                  </span>
                                )}
                                {isSelf && (
                                  <span className="text-[10px] uppercase tracking-widest text-stone-500">(you)</span>
                                )}
                              </div>
                              <div className="text-xs text-stone-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Select
                            value={userType}
                            onValueChange={(v) => changeUserType(u, v)}
                            disabled={isSelf}
                          >
                            <SelectTrigger
                              className="bg-stone-900 border-stone-700 rounded-none h-8 w-40 text-xs"
                              data-testid={`user-type-select-${u.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-900 border-stone-700">
                              {USER_TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-4 text-xs text-stone-500">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                              status === 'approved'
                                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                : status === 'pending'
                                ? 'bg-[#d4af37]/10 border-[#d4af37]/30 text-[#d4af37]'
                                : 'bg-red-500/10 border-red-500/30 text-red-400'
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-stone-400">
                          <div>{u.vineyards_created} vineyards</div>
                          <div className="text-xs text-stone-600">{u.wines_created} wines</div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Switch
                            checked={u.is_admin}
                            disabled={isSelf || pendingId === u.id}
                            onCheckedChange={(v) => toggleAdmin(u, v)}
                            data-testid={`toggle-admin-${u.id}`}
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={isSelf}
                            onClick={() => setConfirmDelete(u)}
                            className="text-stone-500 hover:text-red-400 h-8 w-8 rounded-none"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="pending" className="space-y-10" data-testid="pending-tab-content">
            {/* Pending Users */}
            <PendingSection
              title="New Users Awaiting Approval"
              icon={Users}
              items={pendingUsers}
              emptyText="No pending users"
              renderItem={(u) => (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-stone-800 flex items-center justify-center text-[#d4af37] font-serif">
                      {u.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="text-stone-50 flex items-center gap-2">
                        {u.name}
                        {u.user_type && u.user_type !== 'public' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-[10px] uppercase tracking-widest">
                            {USER_TYPE_BY_VALUE[u.user_type]?.label || u.user_type}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500">{u.email}</div>
                    </div>
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    Joined {new Date(u.created_at).toLocaleString()}
                  </div>
                </>
              )}
              onApprove={(u) => approveUser(u, 'approved')}
              onReject={(u) => approveUser(u, 'rejected')}
              testIdPrefix="pending-user"
            />

            {/* Pending Vineyards */}
            <PendingSection
              title="Vineyards Awaiting Approval"
              icon={MapPin}
              items={pendingVineyards}
              emptyText="No pending vineyards"
              renderItem={(v) => (
                <>
                  <div className="flex gap-4">
                    {v.photos?.[0] && (
                      <img src={v.photos[0]} alt={v.name} className="w-20 h-20 object-cover flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs uppercase tracking-widest text-[#d4af37] mb-1">
                        {v.region}, {v.country}
                      </div>
                      <div className="text-stone-50 font-serif truncate">{v.name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">{v.producer}</div>
                      {v.description && (
                        <p className="text-xs text-stone-600 mt-1 line-clamp-2">{v.description}</p>
                      )}
                    </div>
                  </div>
                </>
              )}
              onApprove={(v) => approveVineyard(v, 'approved')}
              onReject={(v) => approveVineyard(v, 'rejected')}
              onView={(v) => navigate(`/vineyards/${v.id}`)}
              testIdPrefix="pending-vineyard"
            />

            {/* Pending Wines */}
            <PendingSection
              title="Wines Awaiting Approval"
              icon={Wine}
              items={pendingWines}
              emptyText="No pending wines"
              renderItem={(w) => (
                <>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] mb-1">
                    <Grape className="w-3 h-3" /> {w.grape_variety}
                  </div>
                  <div className="text-stone-50 font-serif truncate">{w.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {w.vineyard_name}{w.vintage ? ` · ${w.vintage}` : ''}
                  </div>
                  {w.tasting_notes && (
                    <p className="text-xs text-stone-600 mt-1 line-clamp-2">{w.tasting_notes}</p>
                  )}
                </>
              )}
              onApprove={(w) => approveWine(w, 'approved')}
              onReject={(w) => approveWine(w, 'rejected')}
              onView={(w) => navigate(`/wines/${w.id}`)}
              testIdPrefix="pending-wine"
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="bg-stone-900 border-stone-700" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-50">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-stone-400">
            Permanently delete <span className="text-stone-200">{confirmDelete?.email}</span>?
            Their created vineyards and wines will remain in the system.
          </p>
          <div className="flex gap-4 mt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="flex-1 border-stone-700 rounded-none"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="flex-1 rounded-none"
              data-testid="confirm-delete-user"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
